/**
 * Manuell import av vare-/prisfil fra leverandør.
 *
 * VERQO henter IKKE priser automatisk fra leverandører. Brukeren laster selv
 * ned filen hos sin leverandør og laster den opp her. Teksten under er én
 * kilde til sannhet slik at forsiden, materiellregisteret og importveiviseren
 * forklarer nøyaktig samme fremgangsmåte.
 *
 * Filformatet er CSV.
 */

export const SUPPORTED_PRICE_FILE_EXTENSIONS = ["csv"] as const;

/** Menneskelig lesbar liste over filformatene importen faktisk støtter. */
export const SUPPORTED_PRICE_FILE_LABEL = "CSV (.csv)";

/** Eksempler på leverandører. Kun eksempler – ingen kobling eller integrasjon. */
export const PRICE_FILE_SUPPLIER_EXAMPLES = ["Elektroimportøren", "Byggmakker", "Ahlsell"];

/** Enkelt hovedbudskap om manuell prisfilimport – brukt på forsiden og i onboarding. */
export const PRICE_IMPORT_HEADLINE =
  "Bruk dine egne leverandørpriser i VERQO. Last ned CSV-filen med varer og priser fra leverandøren din, og last den opp her. Bruk varene og prisene videre i kalkyler og tilbud.";

export const PRICE_IMPORT_STEPS = [
  "Last ned vare-/prisfilen som CSV hos leverandøren din.",
  "Trykk «Last opp prisfil» i VERQO.",
  "Velg leverandør og CSV-fil.",
  "Kontroller varer, priser og enheter i forhåndsvisningen.",
  "Bekreft importen.",
] as const;

export const MANUAL_IMPORT_NOTE =
  "Dette er manuell filimport. VERQO henter ikke priser automatisk fra leverandøren. " +
  "Ved nye priser laster du ned en ny CSV-fil og importerer den, og kontrollerer prisendringene før du lagrer.";

export const CSV_FORMAT_NOTE =
  "CSV-filen kan bruke komma eller semikolon som skilletegn, norske tegn og desimalkomma. " +
  "Varenummer, elnummer og GTIN beholdes som tekst, også med innledende nuller.";

export type PriceFileCheck = { ok: boolean; extension: string; message?: string };

function extensionOf(fileName: string): string {
  const name = fileName.trim().toLowerCase();
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1);
}

/** Kontrollerer filnavnet mot formatet importen faktisk klarer å lese. */
export function checkPriceFile(fileName: string): PriceFileCheck {
  const extension = extensionOf(fileName);
  if ((SUPPORTED_PRICE_FILE_EXTENSIONS as readonly string[]).includes(extension)) {
    return { ok: true, extension };
  }
  const what = extension ? `Filtypen .${extension}` : "Filen mangler filendelse og";
  const excelHint =
    extension === "xlsx" || extension === "xls"
      ? "Åpne filen i Excel og velg Fil → Lagre som → CSV, og last opp CSV-filen."
      : "Last ned prisfilen som CSV hos leverandøren, eller åpne filen i regnearket ditt og lagre den som .csv før du prøver på nytt.";
  return {
    ok: false,
    extension,
    message: `${what} støttes ikke. VERQO leser ${SUPPORTED_PRICE_FILE_LABEL}. ${excelHint}`,
  };
}

/** Kolonnene i CSV-malen, med forklaring. Leverandørfiler kan ha andre kolonner. */
export const CSV_TEMPLATE_COLUMNS: { header: string; description: string; example: string }[] = [
  { header: "Varenummer", description: "Leverandørens varenummer, elnummer eller GTIN. Beholdes som tekst.", example: "01234567" },
  { header: "Produktnavn", description: "Navnet på varen. Påkrevd.", example: "Jordfeilautomat 16A" },
  { header: "Enhet", description: "stk, m, pk, rl …", example: "stk" },
  { header: "Innkjøpspris", description: "Din avtalte nettopris. Bruk desimalkomma eller punktum.", example: "629,30" },
  { header: "Listepris", description: "Leverandørens listepris før rabatt (valgfritt).", example: "899,00" },
  { header: "Rabatt %", description: "Din rabatt på listeprisen (valgfritt).", example: "30" },
  { header: "Leverandør", description: "Navn på leverandøren (valgfritt).", example: "Elektroimportøren" },
  { header: "Kategori", description: "Varegruppe i VERQO (valgfritt).", example: "Sikringsmateriell" },
  { header: "Antall i pakning", description: "Hvis prisen gjelder en pakning (valgfritt).", example: "1" },
];

export const CSV_TEMPLATE_FILE_NAME = "verqo-prisfil-mal.csv";

/** Bygger innholdet i den nedlastbare CSV-malen (semikolon, som norsk Excel bruker). */
export function buildCsvTemplate(): string {
  const headers = CSV_TEMPLATE_COLUMNS.map((c) => c.header).join(";");
  const example = CSV_TEMPLATE_COLUMNS.map((c) => c.example).join(";");
  return `${headers}\n${example}\n`;
}
